import { Button, Input } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { Search, UserSearch } from 'lucide-react';
import { type FormEvent, type ReactNode, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EuroCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { EmptyState } from '@/components/feedback/empty-state';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { StatusChip, type StatusChipVariant } from '@/components/ui/status-chip';
import { MatchCell } from '@/features/user-search/match-cell';
import { describeBlockchainMatch } from '@/features/user-search/match-summary';
import { SubaccountMatch } from '@/features/user-search/subaccount-match';
import { useUserBalanceEstimate, useUserSearch } from '@/hooks/queries/users/use-user-search';
import { useQueryErrorToast } from '@/hooks/use-query-error-toast';
import { getErrorMessage } from '@/lib/api/error';
import { type BalanceEstimate, detectType, FAST_ID_REGEX, type QueryType, type SearchUser, type UserSegment, type UserStatus } from '@/services/api/users';
import { formatDateOnly } from '@/utils/format';

// estimate `timestamp` is epoch milliseconds → dd MMM HH:mm
const ESTIMATE_DATE_FORMAT = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });

const QUERY_TYPE_CHIP_VARIANTS: Record<QueryType, StatusChipVariant> = {
  PID: 'info',
  'Fast ID': 'warning',
  Email: 'success',
  Blockchain: 'info',
  Name: 'muted',
};

const SEGMENT_CHIPS: Record<UserSegment, { label: string; variant: StatusChipVariant }> = {
  RETAIL: { label: 'Retail', variant: 'info' },
  INSTITUTIONAL: { label: 'Institutional', variant: 'info' },
  MARKET_MAKER: { label: 'Market Maker', variant: 'warning' },
};

const STATUS_CHIP_VARIANTS: Record<UserStatus, StatusChipVariant> = {
  active: 'success',
  blocked: 'destructive',
  pending: 'warning',
  offboarded: 'muted',
  deleted: 'muted',
};

const isKnownSegment = (segment: string): segment is UserSegment => segment in SEGMENT_CHIPS;
const isKnownStatus = (status: string): status is UserStatus => status in STATUS_CHIP_VARIANTS;

const getDisplayName = ({ firstName, lastName }: SearchUser): string => [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';

const MutedItalic = ({ children }: { children: ReactNode }) => <span className="italic text-muted-foreground">{children}</span>;
MutedItalic.displayName = 'MutedItalic';

const EstimateCell = ({ estimate, isLoading }: { estimate: BalanceEstimate | null; isLoading: boolean }) => {
  if (isLoading) return <MutedItalic>loading…</MutedItalic>;
  if (!estimate) return <MutedItalic>unavailable</MutedItalic>;
  return (
    <span className="flex flex-col">
      <EuroCell value={estimate.estimatedEur} />
      <span className="whitespace-nowrap text-xs text-muted-foreground">as of {ESTIMATE_DATE_FORMAT.format(new Date(estimate.timestamp))}</span>
    </span>
  );
};
EstimateCell.displayName = 'EstimateCell';

interface SubmittedSearch {
  q: string;
  matched: QueryType | null;
}

const UserSearchPage = () => {
  const navigate = useNavigate();
  // Deep-link handoff from the home page search box (?q=...) — read once on mount and
  // auto-run, so landing here from home doesn't require re-typing/re-submitting.
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q')?.trim() ?? '';
  const [input, setInput] = useState(initialQuery);
  const [submitted, setSubmitted] = useState<SubmittedSearch | null>(() => (initialQuery ? { q: initialQuery, matched: detectType(initialQuery) } : null));

  const searchQuery = useUserSearch(submitted?.q ?? '');

  const matched = submitted?.matched ?? null;
  const isExactSearch = matched !== null && matched !== 'Name';

  // A fresh search changes the query key, so results (and the derived estimate
  // fastId below) reset while the new search is in flight.
  const users = searchQuery.data?.users;
  const singleUser = isExactSearch && users?.length === 1 ? users[0] : null;
  const estimateFastId = singleUser?.fastId && FAST_ID_REGEX.test(singleUser.fastId) ? singleUser.fastId : null;

  const balanceQuery = useUserBalanceEstimate(estimateFastId);
  const estimate = balanceQuery.data?.estimate ?? null;
  const isEstimateLoading = estimateFastId !== null && (balanceQuery.isPending || balanceQuery.isFetching);

  useQueryErrorToast(searchQuery, 'Failed to search users');
  useQueryErrorToast(balanceQuery, 'Failed to load balance estimate');

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = input.trim();
    if (!q) return;
    // Type is detected from the submitted text at search time, not live input state.
    const nextMatched = detectType(q);
    if (submitted?.q === q) {
      void searchQuery.refetch();
      if (estimateFastId) void balanceQuery.refetch();
      return;
    }
    setSubmitted({ q, matched: nextMatched });
  };

  const columns = useMemo((): ColumnDef<SearchUser, unknown>[] => {
    const base: ColumnDef<SearchUser, unknown>[] = [
      {
        id: 'name',
        accessorFn: (user) => getDisplayName(user),
        header: 'Name',
        cell: ({ row }) => (
          <span className="flex flex-col gap-1">
            <span className="font-medium">{getDisplayName(row.original)}</span>
            {row.original.subaccount ? <SubaccountMatch subaccount={row.original.subaccount} /> : null}
          </span>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => row.original.email || <MutedItalic>not set</MutedItalic>,
      },
      {
        accessorKey: 'pid',
        header: 'PID',
        enableSorting: false,
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.pid}</span>,
      },
      {
        accessorKey: 'fastId',
        header: 'Fast ID',
        cell: ({ row }) => row.original.fastId ?? '-',
      },
      {
        accessorKey: 'isBroker',
        header: 'Broker',
        cell: ({ row }) => (row.original.isBroker ? <StatusChip variant="warning">Yes</StatusChip> : <StatusChip variant="muted">No</StatusChip>),
      },
      {
        accessorKey: 'created',
        header: 'Created',
        cell: ({ row }) => {
          const created = row.original.created;
          if (!created) return <MutedItalic>unknown</MutedItalic>;
          // `created` is epoch seconds — formatDateOnly's toDate sniffs the magnitude itself
          return <span className="whitespace-nowrap tabular-nums">{formatDateOnly(created)}</span>;
        },
      },
      {
        accessorKey: 'segment',
        header: 'Segment',
        cell: ({ row }) => {
          const segment = row.original.segment;
          if (!isKnownSegment(segment)) return <StatusChip variant="muted">unknown</StatusChip>;
          return <StatusChip variant={SEGMENT_CHIPS[segment].variant}>{SEGMENT_CHIPS[segment].label}</StatusChip>;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          // Deliberate fix: legacy silently fell back to 'active' styling for unknown
          // statuses — render the raw value muted instead so bad data stays visible.
          return <StatusChip variant={isKnownStatus(status) ? STATUS_CHIP_VARIANTS[status] : 'muted'}>{status}</StatusChip>;
        },
      },
    ];
    if (matched === 'Blockchain') {
      base.push({
        id: 'matched-on',
        header: 'Matched on',
        enableSorting: false,
        cell: ({ row }) => <MatchCell user={row.original} />,
      });
    }
    if (isExactSearch) {
      base.push({
        id: 'estimated-portfolio',
        header: 'Est. Portfolio',
        enableSorting: false,
        cell: () => <EstimateCell estimate={estimate} isLoading={isEstimateLoading} />,
      });
    }
    return base;
  }, [matched, isExactSearch, estimate, isEstimateLoading]);

  const liveType = detectType(input);
  const total = searchQuery.data?.total ?? 0;
  const showResultsHeader = submitted !== null && searchQuery.isSuccess && !searchQuery.isFetching;
  const showSearchError = searchQuery.isError && !searchQuery.isFetching;

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <h1 className="text-2xl font-semibold">User Search</h1>

      <form className="flex items-center gap-3" onSubmit={handleSubmit}>
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Search users"
            className="pl-9"
            onChange={(event) => setInput(event.target.value)}
            placeholder="Search by name, email, PID, Fast ID, wallet address, or tx hash"
            value={input}
          />
        </div>
        {liveType ? <StatusChip variant={QUERY_TYPE_CHIP_VARIANTS[liveType]}>{liveType}</StatusChip> : null}
        <Button type="submit">Search</Button>
      </form>

      {submitted === null ? (
        <EmptyState icon={UserSearch} title="Search for a user">
          Enter a name, email address, PID, Fast ID, wallet address, or transaction hash to get started
        </EmptyState>
      ) : (
        <div className="flex flex-col gap-3">
          {showResultsHeader ? (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {total} {total === 1 ? 'result' : 'results'}
              </span>
              {matched ? (
                <StatusChip className="px-2 py-0.5 text-xs" variant={QUERY_TYPE_CHIP_VARIANTS[matched]}>
                  Matched as {matched === 'Blockchain' ? (describeBlockchainMatch(users) ?? matched) : matched}
                </StatusChip>
              ) : null}
            </div>
          ) : null}
          {showSearchError ? (
            <QueryErrorBanner message={`Failed to search users: ${getErrorMessage(searchQuery.error)}`} onRetry={() => searchQuery.refetch()} />
          ) : (
            <DataTable
              columns={columns}
              data={users}
              emptyMessage="No users found"
              isLoading={searchQuery.isFetching}
              onRowClick={(user) => navigate(`/operations/users/${user.pid}`)}
            />
          )}
        </div>
      )}
    </div>
  );
};
UserSearchPage.displayName = 'UserSearchPage';

export default UserSearchPage;
